import {
  presentationRegistry,
  type AnimationInstance,
  type PresentationRegistry,
} from '../core/presentation-registry';

export class AnimationHost {
  #theme: string;
  #registry: PresentationRegistry;
  #target?: HTMLElement;
  #instance?: AnimationInstance;
  #component?: string;
  #paused = false;

  constructor(theme: string, registry = presentationRegistry) {
    this.#theme = theme;
    this.#registry = registry;
  }

  async mount(target: HTMLElement, component: string): Promise<void> {
    this.destroy();
    this.#target = target;
    this.#component = component;
    target.dataset.component = component;
    const factory = this.#registry.animationFactory(this.#theme, component);
    if (!factory) {
      this.showError(target, `Animation unavailable: ${component}`);
      return;
    }
    try {
      this.#instance = factory();
      this.#instance.mount(target);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.showError(target, `Could not start animation: ${message}`);
    }
  }

  play(): void {
    this.#paused = false;
    for (const animation of this.#target?.getAnimations({subtree: true}) ?? []) animation.play();
  }

  pause(): void {
    this.#paused = true;
    for (const animation of this.#target?.getAnimations({subtree: true}) ?? []) animation.pause();
  }

  reset(): void {
    if (!this.#target || !this.#component) return;
    const target = this.#target;
    const component = this.#component;
    void this.mount(target, component).then(() => {
      if (this.#paused) this.pause();
    });
  }

  destroy(): void {
    this.#instance?.unmount();
    this.#target?.replaceChildren();
    this.#instance = undefined;
    this.#target = undefined;
    this.#component = undefined;
  }

  private showError(target: HTMLElement, message: string): void {
    const error = document.createElement('div');
    error.className = 'missing-animation';
    error.textContent = message;
    target.replaceChildren(error);
  }
}
